import { Link } from '@inertiajs/react'
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Package, X, ExternalLink } from 'lucide-react'
import { useOrgPath } from '@/lib/pathFor'
import { formatDate, formatCurrency, formatBookingRef } from '@/lib/formatters'
import { shortContainerType, BOOKING_STATUS_COLORS } from '@/lib/constants'
import { capitalize } from '@/lib/utils'

interface BookingFull {
  id: string
  shipment_id: string
  internal_reference: string
  carrier_booking_id: string | null
  client_email: string | null
  carrier: string | null
  origin_port: string | null
  destination_port: string | null
  sailing_date: string | null
  eta: string | null
  container_count: number | null
  container_type: string | null
  status: string | null
  buy_rate: number | null
  markup: number | null
  sell_rate: number | null
  total_sell: number | null
  transit_days: number | null
}

interface BookingDrawerProps {
  linkedBookingsFull: BookingFull[]
  selectedBookingId: string | null
  onClose: () => void
}

function computeTransitDays(etd: string | null, eta: string | null): string | null {
  if (!etd || !eta) return null
  const diff = Math.round((new Date(eta).getTime() - new Date(etd).getTime()) / (1000 * 60 * 60 * 24))
  return diff > 0 ? `${diff} days` : null
}

function InfoRow({ label, value, bold = false }: { label: string; value: string | null | undefined; bold?: boolean }) {
  if (!value) return null
  return (
    <div className="flex items-center justify-between w-full">
      <span className={`text-[13px] ${bold ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
        {label}
      </span>
      <span className={`text-[13px] text-foreground ${bold ? 'font-semibold' : 'font-medium'}`}>
        {value}
      </span>
    </div>
  )
}

export default function BookingDrawer({ linkedBookingsFull, selectedBookingId, onClose }: BookingDrawerProps) {
  const pathFor = useOrgPath()

  // Find the selected booking from the full list
  const booking = selectedBookingId
    ? linkedBookingsFull.find(b => b.id === selectedBookingId)
    : null

  // Drawer is open when we have a selected booking
  const open = !!booking

  if (!open) return null

  const transit = computeTransitDays(booking.sailing_date, booking.eta)
  const displayReference = formatBookingRef(booking.carrier_booking_id, booking.carrier)

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[480px] sm:w-[480px] p-0 flex flex-col [&>button]:hidden overflow-hidden">
        {/* Header */}
        <div
          className="flex items-center justify-between flex-shrink-0 h-14 px-6 border-b"
        >
          <div className="flex items-center gap-2.5">
            <Package className="h-[18px] w-[18px] text-primary" />
            <SheetTitle className="text-base font-semibold text-foreground font-space-grotesk">
              Booking Details
            </SheetTitle>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Close booking details"
            className="rounded-md"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <SheetDescription className="sr-only">
          Read-only booking details including schedule, pricing, and shipment link.
        </SheetDescription>

        {/* Body */}
        <div
          className="flex flex-col flex-1 overflow-y-auto"
          style={{ gap: 24, padding: 24 }}
        >
          {/* Carrier Booking ID Card */}
          <div className="flex items-center justify-between bg-blue-50 rounded-lg p-4 gap-4">
            <div className="flex flex-col flex-1 gap-0.5">
              <span className="text-xs font-medium text-muted-foreground">
                Internal Ref
              </span>
              <span className="text-lg font-bold text-foreground font-space-grotesk">
                {booking.internal_reference}
              </span>
              <span className="text-xs font-medium text-muted-foreground">
                Carrier Booking ID
              </span>
              <span className="text-sm font-semibold text-foreground font-space-grotesk">
                {displayReference}
              </span>
            </div>
            {booking.status && (
              <Badge variant="secondary" size="sm" className={BOOKING_STATUS_COLORS[booking.status]}>
                {capitalize(booking.status)}
              </Badge>
            )}
          </div>

          {/* Schedule & Route */}
          <div className="flex flex-col gap-3">
            <span className="text-sm font-semibold text-foreground font-space-grotesk">
              Schedule & Route
            </span>
            <div className="flex flex-col w-full rounded-lg border border-border p-4 gap-2">
              <InfoRow label="Route" value={booking.origin_port && booking.destination_port ? `${booking.origin_port} → ${booking.destination_port}` : null} />
              <InfoRow label="Carrier" value={booking.carrier} />
              <div className="h-px bg-border w-full" />
              <InfoRow label="ETD" value={booking.sailing_date ? formatDate(booking.sailing_date) : null} />
              <InfoRow label="ETA" value={booking.eta ? formatDate(booking.eta) : null} />
              <InfoRow label="Transit" value={transit || (booking.transit_days ? `${booking.transit_days} days` : null)} />
            </div>
          </div>

          {/* Containers & Pricing */}
          {(booking.container_count || booking.sell_rate) && (
            <div className="flex flex-col gap-3">
              <span className="text-sm font-semibold text-foreground font-space-grotesk">
                Containers & Pricing
              </span>
              <div className="flex flex-col w-full rounded-lg border border-border p-4 gap-2">
                <InfoRow label="Type" value={booking.container_type ? shortContainerType(booking.container_type) : null} />
                <InfoRow label="Count" value={booking.container_count ? String(booking.container_count) : null} />
                <InfoRow label="Buy Rate (per ctr)" value={booking.buy_rate ? formatCurrency(booking.buy_rate) : null} />
                <InfoRow label="Markup (per ctr)" value={booking.markup ? formatCurrency(booking.markup) : null} />
                <InfoRow label="Sell Rate (per ctr)" value={booking.sell_rate ? formatCurrency(booking.sell_rate) : null} />
                <div className="h-px bg-border w-full" />
                <InfoRow label="Total Sell" value={booking.total_sell ? formatCurrency(booking.total_sell) : null} bold />
              </div>
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* View in Shipment Button */}
          <Link href={pathFor(`/shipments/${booking.shipment_id}`)}>
            <Button className="w-full">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in Shipment
            </Button>
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  )
}
